import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import "./preowned.css";
import Wsp from "../../contact/Component/Floatingwsp";

/* ─── Lucide React icons (real SVG icons) ─── */
import {
  Search, SlidersHorizontal, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, ArrowRight, X,
  RefreshCcw, DollarSign, Zap,
  ShieldCheck, Wrench, ClipboardList, Dna, CreditCard,
  ScanSearch, Car, Home, HelpCircle,
  Fuel, Gauge, Calendar, Settings2, MapPin
} from "lucide-react";

/* ─── Filter Dropdown ─── */
const FilterDrop = ({ label, open, onToggle, children }) => (
  <div className="kpo-drop">
    <button className="kpo-drop__btn" onClick={onToggle}>
      <span>{label}</span>
      {open ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
    </button>
    {open && <div className="kpo-drop__menu">{children}</div>}
  </div>
);

/* ─── Main Component ─── */
const CarPage = (props) => {
  const navigate = useNavigate();

  const [selectedBudget,        setSelectedBudget]        = useState(null);
  const [selectedFuels,         setSelectedFuels]         = useState([]);
  const [selectedTransmissions, setSelectedTransmissions] = useState([]);
  const [selectedOwners,        setSelectedOwners]        = useState([]);
  const [selectedBrand,         setSelectedBrand]         = useState([]);
  const [query,                 setQuery]                 = useState("");

  const [openBrand,  setOpenBrand]  = useState(false);
  const [openBudget, setOpenBudget] = useState(false);
  const [openFuel,   setOpenFuel]   = useState(false);
  const [openTrans,  setOpenTrans]  = useState(false);
  const [openOwner,  setOpenOwner]  = useState(false);
  const [openFaq,    setOpenFaq]    = useState(null);

  const [imgIdx, setImgIdx] = useState([]);

  useEffect(() => {
    setImgIdx(Array(props.oldcar?.all_oldCars?.length || 0).fill(0));
  }, [props.oldcar?.all_oldCars?.length]);

  const debouncedSearch = useCallback(
    debounce((q) => {
      props.viewAllOldCar(selectedBudget, selectedFuels, selectedTransmissions, selectedOwners, q, selectedBrand);
    }, 500), []
  );

  useEffect(() => {
    props.viewAllOldCar(selectedBudget, selectedFuels, selectedTransmissions, selectedOwners, query, selectedBrand);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedBudget, selectedFuels, selectedTransmissions, selectedOwners, query, selectedBrand]);

  const toggleMulti = (setter, name, checked) =>
    setter(prev => checked ? [...prev, name] : prev.filter(x => x !== name));

  const handleClearAll = () => {
    setQuery(""); setSelectedBudget(null);
    setSelectedFuels([]); setSelectedBrand([]);
    setSelectedTransmissions([]); setSelectedOwners([]);
  };

  const handleNextImage = (i) =>
    setImgIdx(prev => prev.map((v, idx) =>
      idx === i ? (v + 1) % props.oldcar.all_oldCars[i].image.length : v));

  const handlePrevImage = (i) =>
    setImgIdx(prev => prev.map((v, idx) =>
      idx === i ? (v - 1 + props.oldcar.all_oldCars[i].image.length) % props.oldcar.all_oldCars[i].image.length : v));

  const handleBudgetSelect = (b) => {
    const val = selectedBudget === b ? null : b;
    setSelectedBudget(val);
    props.viewAllOldCar(val, selectedFuels, selectedTransmissions, selectedOwners, query, selectedBrand);
    setOpenBudget(false);
  };

  const hasFilters = selectedBudget || selectedBrand.length || selectedFuels.length
    || selectedTransmissions.length || selectedOwners.length;

  const cars = props.oldcar?.all_oldCars || [];

  /* ── Data ── */
  const whyCards = [
    {
      Icon: RefreshCcw,
      title: "Exchange Your Legacy",
      desc: "Convert your current vehicle into the gateway for your next chapter. Our evaluation ensures built-in trust in every trade.",
      cta: true,
    },
    {
      Icon: DollarSign,
      title: "Elite Pricing",
      desc: "Market-benchmarked valuations with clear, real-time pricing — complete transparency for buyers and sellers alike.",
    },
    {
      Icon: Zap,
      title: "Instant Liquidity",
      desc: "Rapid documentation and secure ownership transfer so you can move fast and drive sooner.",
    },
  ];

  const confidenceItems = [
    { Icon: ScanSearch,    title: "Elite Inspection",      desc: "Rigorous multi-point inspection by Kia-certified technicians before every car reaches you." },
    { Icon: ShieldCheck,   title: "Extended Assurance",    desc: "Up to a year of comprehensive coverage giving you total peace of mind on every drive." },
    { Icon: Wrench,        title: "Legacy Maintenance",    desc: "Full service history tracking ensuring your vehicle is cared for throughout its lifetime." },
    { Icon: ClipboardList, title: "Radical Transparency",  desc: "Certified pricing with comprehensive disclosure of every vehicle's history — no surprises." },
    { Icon: Dna,           title: "Genuine DNA",           desc: "Genuine Kia accessories and recommended parts keeping your car at peak performance." },
    { Icon: CreditCard,    title: "Fluid Financing",       desc: "Concierge-style EMI options and customised financial solutions tailored to your budget." },
  ];

  const steps = [
    { num: "01", Icon: Car,    title: "Choose Your Kia",   desc: "Browse our certified inventory. Every car is verified, inspected, and ready for a transparent buying experience." },
    { num: "02", Icon: MapPin, title: "Book a Test Drive", desc: "Visit our showroom or schedule a home test drive. Experience transparent pricing with zero pressure." },
    { num: "03", Icon: Home,   title: "Drive Home Today",  desc: "Simple paperwork, instant financing, and doorstep delivery — your car journey ends at your home." },
  ];

  const faqs = [
    { q: "What does Certified Pre-Owned mean?",      a: "A CPO vehicle has passed a rigorous multi-point inspection by Kia-certified technicians and meets strict quality standards before sale." },
    { q: "Can I exchange my non-Kia car?",           a: "Yes! Exchange any make or model for a new Kia. Our team evaluates your car and offers the best possible trade-in value." },
    { q: "Is financing available for used cars?",    a: "Absolutely. We offer easy EMI options and financing packages tailored to your budget — contact our finance team for a personalised quote." },
    { q: "What warranty do I get?",                  a: "All Kia CPO cars come with an assured warranty program. Our executive will walk you through the specific coverage for your chosen model." },
    { q: "Do you provide test drives for used cars?",a: "Yes — we encourage test drives before every purchase. Book online or walk into the showroom anytime." },
  ];

  return (
    <div className="kpo-root" style={{ marginTop: "50px" }}>

      {/* ══ HERO ══ */}
   <div id="banner-image-preowned">
  <div className="kpo-hero__content">
    <span className="kpo-hero__eyebrow">Kia Certified Pre-Owned</span>
    <h1 className="kpo-hero__title">Drive With Confidence</h1>
    <p className="kpo-hero__sub">Quality Without Compromise</p>
    <p className="kpo-hero__desc">
      Every car in our certified collection passes a rigorous multi-point
      inspection — so you get premium ownership without the premium price tag.
    </p>
    <div className="kpo-hero__actions">
      <button
        className="kpo-hero__btn kpo-hero__btn--primary"
        onClick={() => document.querySelector(".kpo-collection-section")
          ?.scrollIntoView({ behavior: "smooth" })}
      >
        Browse Cars
      </button>
      <a href="/contact" className="kpo-hero__btn kpo-hero__btn--ghost">
        Talk to an Expert
      </a>
    </div>
  </div>
</div>

      {/* ══ QUALITY WITHOUT COMPROMISE ══ */}
      <section className="kpo-intro-section">
        <div className="kpo-intro-top">
          <span className="kpo-pill">Certified Excellence</span>
          <h1 className="kpo-intro-heading">Quality Without<br />Compromise</h1>
          <p className="kpo-intro-para">
            The Kia Certified Pre-Owned program sets the benchmark for trust, quality, and premium ownership. Every car passes a rigorous inspection and comes with complete ownership assurance.
          </p>
        </div>

        <div className="kpo-why-grid">
          {whyCards.map(({ Icon, title, desc, cta }) => (
            <div key={title} className="kpo-why-card">
              <div className="kpo-why-card__iconwrap">
                <Icon size={22} strokeWidth={1.8} />
              </div>
              <h3 className="kpo-why-card__title">{title}</h3>
              <p className="kpo-why-card__desc">{desc}</p>
              {cta && (
                <button className="kpo-valuation-btn">
                  Get Valuation <ArrowRight size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ══ THE ELITE COLLECTION ══ */}
      <section className="kpo-collection-section">
        <div className="kpo-collection-header">
          <div>
            <span className="kpo-pill kpo-pill--dark">Premium Stock</span>
            <h2 className="kpo-section-heading">The Elite Collection</h2>
          </div>
          {cars.length > 0 && (
            <p className="kpo-result-count">{cars.length} car{cars.length !== 1 ? "s" : ""} available</p>
          )}
        </div>

        {/* Search */}
        <div className="kpo-searchbar">
          <Search size={16} strokeWidth={2} className="kpo-searchbar__icon" />
          <input
            className="kpo-searchbar__input"
            placeholder="Search by brand, model, fuel type…"
            value={query}
            onChange={e => { setQuery(e.target.value); debouncedSearch(e.target.value); }}
          />
          {query && (
            <button className="kpo-searchbar__clear" onClick={() => setQuery("")}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter row */}
        <div className="kpo-filter-row">
          <div className="kpo-filter-label">
            <SlidersHorizontal size={14} strokeWidth={2} /> Filters:
          </div>

          <FilterDrop label={selectedBrand.length ? `Brand (${selectedBrand.length})` : "Brand"}
            open={openBrand} onToggle={() => setOpenBrand(!openBrand)}>
            {["MG","Maruti","Hyundai","Tata","Kia","Honda","Toyota","Mahindra",
              "Nissan","Ford","Volkswagen","Skoda","Renault","Jeep",
              "Mercedes-Benz","BMW","Audi","Jaguar","Land Rover","Volvo"].map(b => (
              <label key={b} className="kpo-drop__option">
                <input type="checkbox" checked={selectedBrand.includes(b)}
                  onChange={e => toggleMulti(setSelectedBrand, b, e.target.checked)} />
                <span className="kpo-drop__check" />
                {b}
              </label>
            ))}
          </FilterDrop>

          <FilterDrop label={selectedBudget ? selectedBudget : "Budget"}
            open={openBudget} onToggle={() => setOpenBudget(!openBudget)}>
            {["Below ₹3 Lakh","₹3-5 Lakh","₹5-8 Lakh","₹8-12 Lakh","₹12-20 Lakh","₹20 Lakh+"].map(b => (
              <button key={b}
                className={`kpo-drop__option kpo-drop__option--btn${selectedBudget === b ? " active" : ""}`}
                onClick={() => handleBudgetSelect(b)}>
                {b}
              </button>
            ))}
          </FilterDrop>

          <FilterDrop label={selectedFuels.length ? `Fuel (${selectedFuels.length})` : "Fuel"}
            open={openFuel} onToggle={() => setOpenFuel(!openFuel)}>
            {["Petrol","Diesel","CNG","LPG","Electric","Hybrid"].map(f => (
              <label key={f} className="kpo-drop__option">
                <input type="checkbox" checked={selectedFuels.includes(f)}
                  onChange={e => toggleMulti(setSelectedFuels, f, e.target.checked)} />
                <span className="kpo-drop__check" />
                {f}
              </label>
            ))}
          </FilterDrop>

          <FilterDrop label={selectedTransmissions.length ? `Transmission (${selectedTransmissions.length})` : "Transmission"}
            open={openTrans} onToggle={() => setOpenTrans(!openTrans)}>
            {["Automatic","Manual","Clutchless Manual"].map(t => (
              <label key={t} className="kpo-drop__option">
                <input type="checkbox" checked={selectedTransmissions.includes(t)}
                  onChange={e => toggleMulti(setSelectedTransmissions, t, e.target.checked)} />
                <span className="kpo-drop__check" />
                {t}
              </label>
            ))}
          </FilterDrop>

          <FilterDrop label={selectedOwners.length ? `Owners (${selectedOwners.length})` : "Owners"}
            open={openOwner} onToggle={() => setOpenOwner(!openOwner)}>
            {["First Owner","Second Owner","Third or More","Unregistered Car"].map(o => (
              <label key={o} className="kpo-drop__option">
                <input type="checkbox" checked={selectedOwners.includes(o)}
                  onChange={e => toggleMulti(setSelectedOwners, o, e.target.checked)} />
                <span className="kpo-drop__check" />
                {o}
              </label>
            ))}
          </FilterDrop>

          {hasFilters && (
            <button className="kpo-clear-btn" onClick={handleClearAll}>
              <X size={13} /> Clear All
            </button>
          )}
        </div>

        {/* CAR CARDS */}
        <div className="kpo-cars-grid">
          {cars.map((car, index) => (
            <div key={car._id || index} className="kpo-car-card">
              <div className="kpo-car-card__img-wrap">
                <img
                  src={car.image[imgIdx[index] || 0]}
                  alt={car.carName}
                  className="kpo-car-card__img"
                  onClick={() => navigate(`/car-details/${car._id}/${car.brand}`)}
                  loading="lazy"
                />
                {car.image.length > 1 && (
                  <>
                    <button className="kpo-car-card__arr kpo-car-card__arr--l"
                      onClick={() => handlePrevImage(index)}>
                      <ChevronLeft size={16} strokeWidth={2.5} />
                    </button>
                    <button className="kpo-car-card__arr kpo-car-card__arr--r"
                      onClick={() => handleNextImage(index)}>
                      <ChevronRight size={16} strokeWidth={2.5} />
                    </button>
                    <div className="kpo-car-card__dots">
                      {car.image.map((_, di) => (
                        <span key={di} className={`kpo-car-card__dot${(imgIdx[index] || 0) === di ? " active" : ""}`} />
                      ))}
                    </div>
                  </>
                )}
                {car.fuelType && (
                  <span className="kpo-car-card__badge">
                    <Fuel size={10} strokeWidth={2} /> {car.fuelType}
                  </span>
                )}
              </div>

              <div className="kpo-car-card__body">
                {car.brand && <p className="kpo-car-card__brand">{car.brand}</p>}
                <h3 className="kpo-car-card__name"
                  onClick={() => navigate(`/car-details/${car._id}/${car.brand}`)}>
                  {car.carName}
                </h3>

                <div className="kpo-car-card__specs">
                  {car.km && (
                    <span><Gauge size={11} strokeWidth={2} /> {car.km} km</span>
                  )}
                  {car.year && (
                    <span><Calendar size={11} strokeWidth={2} /> {car.year}</span>
                  )}
                  {car.transmission && (
                    <span><Settings2 size={11} strokeWidth={2} /> {car.transmission}</span>
                  )}
                </div>

                <div className="kpo-car-card__pricing">
                  <span className="kpo-car-card__price">₹{car.discountedPrice}</span>
                  {car.price && car.price !== car.discountedPrice && (
                    <span className="kpo-car-card__mrp">₹{car.price}</span>
                  )}
                </div>

                <button className="kpo-car-card__cta"
                  onClick={() => navigate(`/car-details/${car._id}/${car.brand}`)}>
                  Explore <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}

          {cars.length === 0 && (
            <div className="kpo-empty">
              <HelpCircle size={40} strokeWidth={1.5} />
              <p>No cars match your current filters.</p>
              <button onClick={handleClearAll}>Clear All Filters</button>
            </div>
          )}
        </div>
      </section>

      {/* ══ CONFIDENCE IN EVERY MILE ══ */}
      <section className="kpo-confidence-section">
        <div className="kpo-confidence-header">
          <span className="kpo-pill">The Kia Promise</span>
          <h2 className="kpo-section-heading">Confidence In Every Mile</h2>
          <p className="kpo-section-sub">
            Redefining pre-owned ownership through transparency, innovation, and an uncompromising commitment to the Kia standard.
          </p>
        </div>
        <div className="kpo-confidence-grid">
          {confidenceItems.map(({ Icon, title, desc }) => (
            <div key={title} className="kpo-confidence-card">
              <div className="kpo-confidence-card__iconwrap">
                <Icon size={20} strokeWidth={1.8} />
              </div>
              <h4 className="kpo-confidence-card__title">{title}</h4>
              <p className="kpo-confidence-card__desc">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ JOURNEY TO OWNERSHIP ══ */}
      <section className="kpo-journey-section">
        <div className="kpo-journey-left">
          <span className="kpo-pill">Seamless Experience</span>
          <h2 className="kpo-section-heading">Your Journey to<br />Ownership Simplified</h2>
          <div className="kpo-steps">
            {steps.map(({ num, Icon, title, desc }) => (
              <div key={num} className="kpo-step">
                <div className="kpo-step__left">
                  <div className="kpo-step__iconwrap">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <div className="kpo-step__line" />
                </div>
                <div className="kpo-step__content">
                  <span className="kpo-step__num">{num}</span>
                  <h4 className="kpo-step__title">{title}</h4>
                  <p className="kpo-step__desc">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="kpo-journey-right">
          <div className="kpo-journey-right__card">
            <h3>Why buy from Central Kia?</h3>
            <p>
              We have streamlined the pre-owned car buying process into three simple steps. No hidden fees, no complicated paperwork — just pure transparency from the moment you choose your car to the day it sits in your driveway.
            </p>
            <ul className="kpo-journey-bullets">
              <li><ShieldCheck size={15} strokeWidth={2} /> 100% transparent pricing</li>
              <li><CreditCard size={15} strokeWidth={2} /> Easy EMI &amp; instant financing</li>
              <li><Wrench size={15} strokeWidth={2} /> Post-sale service support</li>
              <li><ClipboardList size={15} strokeWidth={2} /> Complete documentation assistance</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section className="kpo-faq-section">
        <span className="kpo-pill kpo-center-pill">Got Questions?</span>
        <h2 className="kpo-section-heading kpo-center">Frequently Asked Questions</h2>
        <div className="kpo-faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`kpo-faq-item${openFaq === i ? " open" : ""}`}>
              <button className="kpo-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <HelpCircle size={16} strokeWidth={1.8} className="kpo-faq-ico" />
                <span>{f.q}</span>
                {openFaq === i
                  ? <ChevronUp size={16} strokeWidth={2} className="kpo-faq-arrow" />
                  : <ChevronDown size={16} strokeWidth={2} className="kpo-faq-arrow" />
                }
              </button>
              {openFaq === i && <p className="kpo-faq-a">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      <Wsp />
    </div>
  );
};

export default CarPage;